import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';

export type StepTestStopReason =
  | 'patient_request'
  | 'symptom_limitation'
  | 'safety_stop'
  | 'supervisor_interruption'
  | 'pace_loss'
  | 'completed';

export interface StepTestVitalsBlock {
  hr: string | null;
  spo2: string | null;
  bp: {
    sys: string | null;
    dia: string | null;
  };
  borg: {
    dyspnea: number | null;
    legFatigue: number | null;
  };
}

export interface StepTestPauseInterval {
  from_s: number;
  to_s: number;
  duration_s: number;
  from_fmt: string;
  to_fmt: string;
  duration_fmt: string;
  reason: StepTestStopReason | null;
  reason_label: string | null;
}

export interface StepTestPauseReport {
  total_stopped_s: number;
  total_stopped_fmt: string;
  intervals: StepTestPauseInterval[];
}

export interface StepTestTimerBlock {
  elapsed_s: number;
  completed: boolean;
  startedAt: string | null;
  endedAt: string | null;
}

export interface SixMSTMinuteReading {
  minute: number;
  hr: string | null;
  spo2: string | null;
  totalSteps: number | null;
  notes?: string | null;
}

export interface SixMSTProtocol {
  stepHeightCm: number;
  durationMinutes: number;
  cadence: 'free';
  handSupportAllowed: boolean;
  alternatingLegsAllowed: boolean;
}

export interface SixMSTResultPayload {
  protocol: SixMSTProtocol;
  baseline: StepTestVitalsBlock;
  perMinute: SixMSTMinuteReading[];
  final: StepTestVitalsBlock;
  totalSteps: number;
  stop_report: StepTestPauseReport;
  timer: StepTestTimerBlock;
  notes?: string;
  finishedAt: string;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
  report?: {
    interpretation?: string;
    referenceSource?: string;
  };
}

export interface SixMSTSummary {
  totalSteps: number;
  predictedSteps: number | null;
  percentPredicted: number | null;
  normativeMean: number | null;
  normativeP10: number | null;
  normativeBandLabel: string | null;
  belowNormativeLowerLimit: boolean;
  minSpo2: number | null;
  spo2Drop: number | null;
  peakHr: number | null;
  totalStopped_s: number;
  interruptionCount: number;
  severity: string;
}

export type ChesterTerminationReason =
  | 'completed_all_levels'
  | 'symptom_limitation'
  | 'pace_loss'
  | 'hr_limit'
  | 'supervisor_interruption'
  | 'patient_request';

export interface ChesterLevelResult {
  level: 1 | 2 | 3 | 4 | 5;
  cadenceStepsPerMinute: number;
  durationSeconds: number;
  completed: boolean;
  hr: string | null;
  spo2: string | null;
  borgDyspnea: number | null;
  borgLegFatigue: number | null;
  totalSteps: number | null;
  notes?: string | null;
}

export interface ChesterProtocol {
  stepHeightCm: number;
  totalLevels: 5;
  levelDurationSeconds: number;
  cadenceStepsPerMinute: [15, 20, 25, 30, 35];
  audioPaced: boolean;
  hrStopPercent: number;
}

export interface ChesterResultPayload {
  protocol: ChesterProtocol;
  baseline: StepTestVitalsBlock;
  levels: ChesterLevelResult[];
  final: StepTestVitalsBlock;
  totalSteps: number;
  achievedLevel: number;
  terminationReason: ChesterTerminationReason;
  completedAllLevels: boolean;
  timer: StepTestTimerBlock;
  notes?: string;
  finishedAt: string;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
  report?: {
    interpretation?: string;
    referenceSource?: string;
  };
}

export interface ChesterSummary {
  totalSteps: number;
  achievedLevel: number;
  completedLevels: number;
  completedAllLevels: boolean;
  maxCadence: number | null;
  minSpo2: number | null;
  peakHr: number | null;
  terminationReasonLabel: string;
  severity: string;
}

type SixMSTNormativeBand = {
  minAge: number;
  maxAge: number;
  label: string;
  male: { mean: number; p10: number };
  female: { mean: number; p10: number };
};

const SIX_MST_REFERENCE_SOURCE =
  'Albuquerque et al. J Bras Pneumol. 2022;48(4):e20210511.';

const SIX_MST_NORMATIVE_BANDS: SixMSTNormativeBand[] = [
  { minAge: 18, maxAge: 28, label: '18-28 anos', male: { mean: 217, p10: 171 }, female: { mean: 190, p10: 150 } },
  { minAge: 29, maxAge: 39, label: '29-39 anos', male: { mean: 200, p10: 159 }, female: { mean: 185, p10: 137 } },
  { minAge: 40, maxAge: 49, label: '40-49 anos', male: { mean: 177, p10: 141 }, female: { mean: 163, p10: 113 } },
  { minAge: 50, maxAge: 59, label: '50-59 anos', male: { mean: 176, p10: 137 }, female: { mean: 154, p10: 122 } },
  { minAge: 60, maxAge: 69, label: '60-69 anos', male: { mean: 153, p10: 91 }, female: { mean: 127, p10: 96 } },
  { minAge: 70, maxAge: 79, label: '70-79 anos', male: { mean: 147, p10: 107 }, female: { mean: 104, p10: 80 } },
];

const CHESTER_PROTOCOL: ChesterProtocol = {
  stepHeightCm: 17,
  totalLevels: 5,
  levelDurationSeconds: 120,
  cadenceStepsPerMinute: [15, 20, 25, 30, 35],
  audioPaced: true,
  hrStopPercent: 90,
};

const SIX_MST_PROTOCOL: SixMSTProtocol = {
  stepHeightCm: 20,
  durationMinutes: 6,
  cadence: 'free',
  handSupportAllowed: false,
  alternatingLegsAllowed: true,
};

const STEP_TEST_STOP_REASON_LABELS: Record<StepTestStopReason, string> = {
  patient_request: 'Interrupção a pedido do paciente',
  symptom_limitation: 'Sintomas limitaram a continuidade',
  safety_stop: 'Encerrado por critério de segurança',
  supervisor_interruption: 'Interrompido pelo avaliador',
  pace_loss: 'Perda da cadência proposta',
  completed: 'Sem interrupções relevantes',
};

const CHESTER_TERMINATION_LABELS: Record<ChesterTerminationReason, string> = {
  completed_all_levels: 'Completou todos os níveis',
  symptom_limitation: 'Encerrado por limitação sintomática',
  pace_loss: 'Encerrado por perda da cadência',
  hr_limit: 'Encerrado por atingir a FC limite',
  supervisor_interruption: 'Encerrado pelo avaliador',
  patient_request: 'Encerrado a pedido do paciente',
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const readNonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

export const hasRecordedStepTestText = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const normalized = value.trim();
  return normalized.length > 0 && normalized !== '-' && normalized !== '—';
};

export const hasSixMSTMinuteReadingData = (entry: SixMSTMinuteReading): boolean =>
  hasRecordedStepTestText(entry.hr) ||
  hasRecordedStepTestText(entry.spo2) ||
  entry.totalSteps != null ||
  hasRecordedStepTestText(entry.notes);

export const hasStepTestVitalsData = (block: StepTestVitalsBlock): boolean =>
  hasRecordedStepTestText(block.hr) ||
  hasRecordedStepTestText(block.spo2) ||
  hasRecordedStepTestText(block.bp.sys) ||
  hasRecordedStepTestText(block.bp.dia) ||
  block.borg.dyspnea != null ||
  block.borg.legFatigue != null;

const normalizeSex = (value: unknown): 'M' | 'F' | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  if (normalized === 'M' || normalized === 'MASCULINO' || normalized === 'MALE') return 'M';
  if (normalized === 'F' || normalized === 'FEMININO' || normalized === 'FEMALE') return 'F';
  return null;
};

const formatClock = (seconds: number): string => {
  const safe = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const normalizeVitalsBlock = (value: unknown): StepTestVitalsBlock => {
  const source = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const bp = source.bp && typeof source.bp === 'object' ? (source.bp as Record<string, unknown>) : {};
  const borg = source.borg && typeof source.borg === 'object' ? (source.borg as Record<string, unknown>) : {};

  return {
    hr: readNonEmptyString(source.hr),
    spo2: readNonEmptyString(source.spo2),
    bp: {
      sys: readNonEmptyString(bp.sys),
      dia: readNonEmptyString(bp.dia),
    },
    borg: {
      dyspnea: toFiniteNumber(borg.dyspnea),
      legFatigue: toFiniteNumber(borg.legFatigue),
    },
  };
};

const normalizeTimerBlock = (value: unknown, expectedSeconds: number): StepTestTimerBlock => {
  const source = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const elapsed = toFiniteNumber(source.elapsed_s) ?? 0;
  const startedAt = readNonEmptyString(source.startedAt);
  const endedAt = readNonEmptyString(source.endedAt);

  return {
    elapsed_s: Math.max(0, Math.round(elapsed)),
    completed: source.completed === true || elapsed >= expectedSeconds,
    startedAt,
    endedAt,
  };
};

const normalizePauseReport = (value: unknown): StepTestPauseReport => {
  const source = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const rawIntervals = Array.isArray(source.intervals) ? source.intervals : [];
  const intervals = rawIntervals
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const item = entry as Record<string, unknown>;
      const from = toFiniteNumber(item.from_s) ?? 0;
      const to = toFiniteNumber(item.to_s) ?? from;
      const duration = Math.max(0, (toFiniteNumber(item.duration_s) ?? to - from));
      const reason = readNonEmptyString(item.reason) as StepTestStopReason | null;
      return {
        from_s: from,
        to_s: to,
        duration_s: duration,
        from_fmt: formatClock(from),
        to_fmt: formatClock(to),
        duration_fmt: formatClock(duration),
        reason,
        reason_label: reason ? STEP_TEST_STOP_REASON_LABELS[reason] : null,
      } satisfies StepTestPauseInterval;
    })
    .filter((item): item is StepTestPauseInterval => item != null);
  const totalStopped = intervals.reduce((sum, item) => sum + item.duration_s, 0);
  return {
    total_stopped_s: totalStopped,
    total_stopped_fmt: formatClock(totalStopped),
    intervals,
  };
};

const getNumberSeriesMin = (values: Array<number | null | undefined>): number | null => {
  const filtered = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  return filtered.length ? Math.min(...filtered) : null;
};

const getNumberSeriesMax = (values: Array<number | null | undefined>): number | null => {
  const filtered = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  return filtered.length ? Math.max(...filtered) : null;
};

export const formatStepStopReasonLabel = (reason?: StepTestStopReason | null): string =>
  reason ? STEP_TEST_STOP_REASON_LABELS[reason] : 'Motivo não informado';

export const formatChesterTerminationReasonLabel = (reason?: ChesterTerminationReason | null): string =>
  reason ? CHESTER_TERMINATION_LABELS[reason] : 'Encerramento não informado';

export const formatBorgValue = (value: number | null | undefined): string => {
  if (value == null) return '—';
  return String(value).replace('.', ',');
};

export const getSixMSTReferenceEquation = (patientContext?: Record<string, unknown> | null): number | null => {
  const age = toFiniteNumber(patientContext?.ageYears);
  const height = toFiniteNumber(patientContext?.heightCm);
  const weight = toFiniteNumber(patientContext?.weightKg);
  const sex = normalizeSex(patientContext?.sex);

  if (age == null || height == null || weight == null || sex == null) return null;
  const sexCode = sex === 'M' ? 1 : 0;
  return +(106 + 17.02 * sexCode - 1.24 * age + 0.8 * height - 0.39 * weight).toFixed(1);
};

export const getSixMSTNormativeBand = (patientContext?: Record<string, unknown> | null) => {
  const age = toFiniteNumber(patientContext?.ageYears);
  const sex = normalizeSex(patientContext?.sex);
  if (age == null || sex == null) return null;
  const band = SIX_MST_NORMATIVE_BANDS.find((item) => age >= item.minAge && age <= item.maxAge);
  if (!band) return null;
  const values = sex === 'M' ? band.male : band.female;
  return {
    label: band.label,
    mean: values.mean,
    p10: values.p10,
    sex,
  };
};

export const normalizeSixMSTPayload = (value: unknown): SixMSTResultPayload | null => {
  if (!value || typeof value !== 'object') return null;
  const source = value as Record<string, unknown>;
  const rawMinutes = Array.isArray(source.perMinute) ? source.perMinute : [];
  const perMinute = rawMinutes
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') return null;
      const item = entry as Record<string, unknown>;
      return {
        minute: Math.max(1, Math.min(6, Math.round(toFiniteNumber(item.minute) ?? index + 1))),
        hr: readNonEmptyString(item.hr),
        spo2: readNonEmptyString(item.spo2),
        totalSteps: toFiniteNumber(item.totalSteps),
        notes: readNonEmptyString(item.notes),
      } satisfies SixMSTMinuteReading;
    })
    .filter((item): item is SixMSTMinuteReading => item != null);
  const protocolSource = source.protocol && typeof source.protocol === 'object' ? (source.protocol as Record<string, unknown>) : {};
  return {
    protocol: {
      stepHeightCm: toFiniteNumber(protocolSource.stepHeightCm) ?? SIX_MST_PROTOCOL.stepHeightCm,
      durationMinutes: toFiniteNumber(protocolSource.durationMinutes) ?? SIX_MST_PROTOCOL.durationMinutes,
      cadence: 'free',
      handSupportAllowed: protocolSource.handSupportAllowed === true,
      alternatingLegsAllowed: protocolSource.alternatingLegsAllowed !== false,
    },
    baseline: normalizeVitalsBlock(source.baseline),
    perMinute,
    final: normalizeVitalsBlock(source.final),
    totalSteps: Math.max(0, Math.round(toFiniteNumber(source.totalSteps) ?? 0)),
    stop_report: normalizePauseReport(source.stop_report),
    timer: normalizeTimerBlock(source.timer, 360),
    notes: readNonEmptyString(source.notes) ?? undefined,
    finishedAt: readNonEmptyString(source.finishedAt) ?? new Date().toISOString(),
    patientContext: source.patientContext && typeof source.patientContext === 'object' ? (source.patientContext as Record<string, unknown>) : undefined,
    professionalContext:
      source.professionalContext && typeof source.professionalContext === 'object'
        ? (source.professionalContext as Record<string, unknown>)
        : undefined,
    report: source.report && typeof source.report === 'object' ? (source.report as { interpretation?: string; referenceSource?: string }) : undefined,
  };
};

export const buildSixMSTPayload = (params: {
  protocol?: Partial<SixMSTProtocol>;
  baseline: StepTestVitalsBlock;
  perMinute: SixMSTMinuteReading[];
  final: StepTestVitalsBlock;
  totalSteps: number;
  stopReport?: StepTestPauseReport;
  timer: StepTestTimerBlock;
  notes?: string;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
  finishedAt?: string;
}): SixMSTResultPayload => ({
  protocol: {
    ...SIX_MST_PROTOCOL,
    ...(params.protocol ?? {}),
  },
  baseline: params.baseline,
  perMinute: params.perMinute,
  final: params.final,
  totalSteps: Math.max(0, Math.round(params.totalSteps)),
  stop_report: params.stopReport ?? { total_stopped_s: 0, total_stopped_fmt: '00:00', intervals: [] },
  timer: params.timer,
  notes: params.notes?.trim() || undefined,
  patientContext: params.patientContext,
  professionalContext: params.professionalContext,
  finishedAt: params.finishedAt ?? new Date().toISOString(),
  report: {
    referenceSource: SIX_MST_REFERENCE_SOURCE,
  },
});

export const summarizeSixMSTResult = (value: unknown): SixMSTSummary => {
  const payload = normalizeSixMSTPayload(value);
  if (!payload) {
    return {
      totalSteps: 0,
      predictedSteps: null,
      percentPredicted: null,
      normativeMean: null,
      normativeP10: null,
      normativeBandLabel: null,
      belowNormativeLowerLimit: false,
      minSpo2: null,
      spo2Drop: null,
      peakHr: null,
      totalStopped_s: 0,
      interruptionCount: 0,
      severity: 'Sem dados',
    };
  }

  const predictedSteps = getSixMSTReferenceEquation(payload.patientContext);
  const normativeBand = getSixMSTNormativeBand(payload.patientContext);
  const percentPredicted = predictedSteps && payload.totalSteps > 0 ? +(payload.totalSteps / predictedSteps * 100).toFixed(1) : null;
  const spo2Values = [
    toFiniteNumber(payload.baseline.spo2),
    ...payload.perMinute.map((item) => toFiniteNumber(item.spo2)),
    toFiniteNumber(payload.final.spo2),
  ];
  const hrValues = [
    toFiniteNumber(payload.baseline.hr),
    ...payload.perMinute.map((item) => toFiniteNumber(item.hr)),
    toFiniteNumber(payload.final.hr),
  ];
  const minSpo2 = getNumberSeriesMin(spo2Values);
  const peakHr = getNumberSeriesMax(hrValues);
  const baselineSpo2 = toFiniteNumber(payload.baseline.spo2);
  const spo2Drop = baselineSpo2 != null && minSpo2 != null ? +(baselineSpo2 - minSpo2).toFixed(1) : null;
  const belowNormativeLowerLimit = normativeBand != null && payload.totalSteps < normativeBand.p10;

  let severity = 'Resposta estável';
  if (!payload.timer.completed && payload.totalSteps > 0) severity = 'Teste interrompido';
  if (payload.stop_report.intervals.length > 0) severity = 'Tolerância reduzida';
  if (minSpo2 != null && minSpo2 <= 88) severity = 'Dessaturação importante';
  if (belowNormativeLowerLimit) severity = 'Abaixo do limite inferior';

  return {
    totalSteps: payload.totalSteps,
    predictedSteps,
    percentPredicted,
    normativeMean: normativeBand?.mean ?? null,
    normativeP10: normativeBand?.p10 ?? null,
    normativeBandLabel: normativeBand?.label ?? null,
    belowNormativeLowerLimit,
    minSpo2,
    spo2Drop,
    peakHr,
    totalStopped_s: payload.stop_report.total_stopped_s,
    interruptionCount: payload.stop_report.intervals.length,
    severity,
  };
};

export const resolveSixMSTInterpretation = (
  value: unknown,
  options?: { includeNormativeValues?: boolean },
): string => {
  const payload = normalizeSixMSTPayload(value);
  const summary = summarizeSixMSTResult(value);
  if (!payload) return 'Sem dados suficientes para interpretar o teste do degrau de 6 minutos.';

  const parts: string[] = [`Foram registradas ${summary.totalSteps} subidas no degrau em 6 minutos.`];
  if (summary.predictedSteps != null && summary.percentPredicted != null) {
    parts.push(
      `Pelo modelo de Albuquerque et al. 2022, o valor previsto é ${summary.predictedSteps.toFixed(1)} degraus (${summary.percentPredicted.toFixed(1)}% do previsto).`,
    );
  }
  if (summary.normativeBandLabel && summary.normativeP10 != null) {
    if (options?.includeNormativeValues && summary.normativeMean != null) {
      parts.push(
        summary.belowNormativeLowerLimit
          ? `O desempenho ficou abaixo do limite inferior de ${summary.normativeP10} degraus (10º percentil) esperado para ${summary.normativeBandLabel}; a média nessa faixa é ${summary.normativeMean} degraus.`
          : `O desempenho permaneceu acima do limite inferior de ${summary.normativeP10} degraus (10º percentil) esperado para ${summary.normativeBandLabel}; a média nessa faixa é ${summary.normativeMean} degraus.`,
      );
    } else {
      parts.push(
        summary.belowNormativeLowerLimit
          ? `O desempenho ficou abaixo do limite inferior esperado (10º percentil) para ${summary.normativeBandLabel}.`
          : `O desempenho permaneceu acima do limite inferior esperado (10º percentil) para ${summary.normativeBandLabel}.`,
      );
    }
  }
  if (summary.minSpo2 != null) {
    parts.push(
      summary.spo2Drop != null && summary.spo2Drop >= 4
        ? `Houve queda de SpO2 de ${summary.spo2Drop.toFixed(1)}%, com nadir de ${summary.minSpo2.toFixed(1)}%.`
        : `A menor SpO2 registrada foi ${summary.minSpo2.toFixed(1)}%.`,
    );
  }
  if (summary.totalStopped_s > 0) {
    parts.push(`O paciente permaneceu parado por ${formatClock(summary.totalStopped_s)} ao longo do teste.`);
  }
  parts.push(`Situação observada: ${summary.severity.toLowerCase()}.`);
  return resolveAssessmentInterpretationText(payload, parts.join(' '));
};

const normalizeChesterLevel = (value: unknown, index: number): ChesterLevelResult => {
  const cadence = CHESTER_PROTOCOL.cadenceStepsPerMinute[index] ?? 15;
  const source = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return {
    level: (index + 1) as ChesterLevelResult['level'],
    cadenceStepsPerMinute: toFiniteNumber(source.cadenceStepsPerMinute) ?? cadence,
    durationSeconds: toFiniteNumber(source.durationSeconds) ?? CHESTER_PROTOCOL.levelDurationSeconds,
    completed: source.completed === true,
    hr: readNonEmptyString(source.hr),
    spo2: readNonEmptyString(source.spo2),
    borgDyspnea: toFiniteNumber(source.borgDyspnea),
    borgLegFatigue: toFiniteNumber(source.borgLegFatigue),
    totalSteps: toFiniteNumber(source.totalSteps),
    notes: readNonEmptyString(source.notes),
  };
};

export const normalizeChesterPayload = (value: unknown): ChesterResultPayload | null => {
  if (!value || typeof value !== 'object') return null;
  const source = value as Record<string, unknown>;
  const rawLevels = Array.isArray(source.levels) ? source.levels : [];
  const levels = Array.from({ length: 5 }, (_, index) => normalizeChesterLevel(rawLevels[index], index));
  const protocolSource = source.protocol && typeof source.protocol === 'object' ? (source.protocol as Record<string, unknown>) : {};
  const completedLevels = levels.filter((item) => item.completed).length;
  const totalSteps =
    Math.round(
      toFiniteNumber(source.totalSteps)
        ?? levels.reduce((sum, level) => sum + (typeof level.totalSteps === 'number' ? level.totalSteps : 0), 0),
    ) || 0;
  const completedAllLevels = source.completedAllLevels === true || completedLevels === 5;
  return {
    protocol: {
      ...CHESTER_PROTOCOL,
      stepHeightCm: toFiniteNumber(protocolSource.stepHeightCm) ?? CHESTER_PROTOCOL.stepHeightCm,
      levelDurationSeconds: toFiniteNumber(protocolSource.levelDurationSeconds) ?? CHESTER_PROTOCOL.levelDurationSeconds,
      hrStopPercent: toFiniteNumber(protocolSource.hrStopPercent) ?? CHESTER_PROTOCOL.hrStopPercent,
    },
    baseline: normalizeVitalsBlock(source.baseline),
    levels,
    final: normalizeVitalsBlock(source.final),
    totalSteps: Math.max(0, totalSteps),
    achievedLevel: Math.max(0, Math.min(5, Math.round(toFiniteNumber(source.achievedLevel) ?? completedLevels))),
    terminationReason:
      (readNonEmptyString(source.terminationReason) as ChesterTerminationReason | null) ??
      (completedAllLevels ? 'completed_all_levels' : 'patient_request'),
    completedAllLevels,
    timer: normalizeTimerBlock(source.timer, 600),
    notes: readNonEmptyString(source.notes) ?? undefined,
    finishedAt: readNonEmptyString(source.finishedAt) ?? new Date().toISOString(),
    patientContext: source.patientContext && typeof source.patientContext === 'object' ? (source.patientContext as Record<string, unknown>) : undefined,
    professionalContext:
      source.professionalContext && typeof source.professionalContext === 'object'
        ? (source.professionalContext as Record<string, unknown>)
        : undefined,
    report: source.report && typeof source.report === 'object' ? (source.report as { interpretation?: string; referenceSource?: string }) : undefined,
  };
};

export const buildChesterPayload = (params: {
  protocol?: Partial<ChesterProtocol>;
  baseline: StepTestVitalsBlock;
  levels: ChesterLevelResult[];
  final: StepTestVitalsBlock;
  totalSteps?: number;
  achievedLevel?: number;
  terminationReason: ChesterTerminationReason;
  completedAllLevels?: boolean;
  timer: StepTestTimerBlock;
  notes?: string;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
  finishedAt?: string;
}): ChesterResultPayload => {
  const levels = Array.from({ length: 5 }, (_, index) => normalizeChesterLevel(params.levels[index], index));
  const completedLevels = levels.filter((item) => item.completed).length;
  const totalSteps =
    params.totalSteps != null
      ? Math.max(0, Math.round(params.totalSteps))
      : levels.reduce((sum, level) => sum + (typeof level.totalSteps === 'number' ? level.totalSteps : 0), 0);
  const completedAllLevels = params.completedAllLevels === true || completedLevels === 5;
  return {
    protocol: {
      ...CHESTER_PROTOCOL,
      ...(params.protocol ?? {}),
    },
    baseline: params.baseline,
    levels,
    final: params.final,
    totalSteps,
    achievedLevel: params.achievedLevel ?? completedLevels,
    terminationReason: completedAllLevels ? 'completed_all_levels' : params.terminationReason,
    completedAllLevels,
    timer: params.timer,
    notes: params.notes?.trim() || undefined,
    patientContext: params.patientContext,
    professionalContext: params.professionalContext,
    finishedAt: params.finishedAt ?? new Date().toISOString(),
    report: {
      referenceSource:
        'Karloh et al. Braz J Phys Ther. 2013;17(3):227-235. Protocolo incremental com degrau de 17 cm e até 5 níveis.',
    },
  };
};

export const summarizeChesterResult = (value: unknown): ChesterSummary => {
  const payload = normalizeChesterPayload(value);
  if (!payload) {
    return {
      totalSteps: 0,
      achievedLevel: 0,
      completedLevels: 0,
      completedAllLevels: false,
      maxCadence: null,
      minSpo2: null,
      peakHr: null,
      terminationReasonLabel: formatChesterTerminationReasonLabel(null),
      severity: 'Sem dados',
    };
  }

  const completedLevels = payload.levels.filter((item) => item.completed).length;
  const activeLevels = payload.levels.filter((item) => item.completed || item.totalSteps != null);
  const minSpo2 = getNumberSeriesMin([
    toFiniteNumber(payload.baseline.spo2),
    ...activeLevels.map((item) => toFiniteNumber(item.spo2)),
    toFiniteNumber(payload.final.spo2),
  ]);
  const peakHr = getNumberSeriesMax([
    toFiniteNumber(payload.baseline.hr),
    ...activeLevels.map((item) => toFiniteNumber(item.hr)),
    toFiniteNumber(payload.final.hr),
  ]);
  const maxCadence = getNumberSeriesMax(activeLevels.map((item) => item.cadenceStepsPerMinute));

  let severity = 'Teste concluído';
  if (!payload.completedAllLevels) severity = 'Interrompido antes do nível final';
  if (payload.terminationReason === 'pace_loss') severity = 'Interrompido por perda de cadência';
  if (payload.terminationReason === 'symptom_limitation') severity = 'Interrompido por sintomas';
  if (payload.terminationReason === 'hr_limit' || payload.terminationReason === 'supervisor_interruption') {
    severity = 'Interrompido por segurança';
  }

  return {
    totalSteps: payload.totalSteps,
    achievedLevel: payload.achievedLevel,
    completedLevels,
    completedAllLevels: payload.completedAllLevels,
    maxCadence,
    minSpo2,
    peakHr,
    terminationReasonLabel: formatChesterTerminationReasonLabel(payload.terminationReason),
    severity,
  };
};

export const resolveChesterInterpretation = (value: unknown): string => {
  const payload = normalizeChesterPayload(value);
  const summary = summarizeChesterResult(value);
  if (!payload) return 'Sem dados suficientes para interpretar o Chester Step Test.';

  const parts: string[] = [
    `O paciente concluiu ${summary.completedLevels} de ${payload.protocol.totalLevels} níveis do Chester Step Test, com ${summary.totalSteps} ciclos de subida e descida registrados.`,
  ];
  if (summary.maxCadence != null) {
    parts.push(`A maior cadência sustentada foi ${summary.maxCadence} passos por minuto.`);
  }
  if (summary.minSpo2 != null) {
    parts.push(`A menor SpO2 registrada foi ${summary.minSpo2.toFixed(1)}%.`);
  }
  if (summary.peakHr != null) {
    parts.push(`A FC pico observada foi ${summary.peakHr.toFixed(0)} bpm.`);
  }
  parts.push(`Encerramento: ${summary.terminationReasonLabel.toLowerCase()}.`);
  parts.push(`Situação observada: ${summary.severity.toLowerCase()}.`);
  return resolveAssessmentInterpretationText(payload, parts.join(' '));
};

export const getDefaultChesterLevels = (): ChesterLevelResult[] =>
  CHESTER_PROTOCOL.cadenceStepsPerMinute.map((cadence, index) => ({
    level: (index + 1) as ChesterLevelResult['level'],
    cadenceStepsPerMinute: cadence,
    durationSeconds: CHESTER_PROTOCOL.levelDurationSeconds,
    completed: false,
    hr: null,
    spo2: null,
    borgDyspnea: null,
    borgLegFatigue: null,
    totalSteps: null,
    notes: null,
  }));

export const SIX_MST_REFERENCE_METADATA = {
  source: SIX_MST_REFERENCE_SOURCE,
  equationLabel: '6MST = 106 + 17.02(sexo) - 1.24(idade) + 0.8(altura) - 0.39(peso)',
  equationDescription:
    'Sexo codificado como 0 para mulher e 1 para homem; idade em anos; altura em cm; peso em kg.',
};

export const CHESTER_PROTOCOL_METADATA = {
  source: 'Karloh et al. Braz J Phys Ther. 2013;17(3):227-235.',
  description:
    'Degrau de 17 cm, progressão incremental com cadências de 15, 20, 25, 30 e 35 passos/min, até 5 níveis e máximo de 10 minutos.',
};
